import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { useNavigate } from 'react-router-dom';
import { fileUrl } from '../api';
import './myExpenses.css';

const PAGE_SIZE = 100;
// Inline FX Override Component to avoid import issues
const FxOverrideModal = ({ 
  fetchedRate, 
  currentRate, 
  originalAmount, 
  originalCurrency, 
  reimbursementCurrency, 
  onOverrideApplied, 
  onClose 
}: {
  fetchedRate: number;
  currentRate: number;
  originalAmount: number;
  originalCurrency: string;
  reimbursementCurrency: string;
  onOverrideApplied: () => void;
  onClose: () => void;
}) => {
  const [inputMode, setInputMode] = useState('amount');
  const [overrideRate, setOverrideRate] = useState(currentRate.toString());
  const [reimbursementAmount, setReimbursementAmount] = useState((originalAmount * currentRate).toFixed(2));
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');

  const expectedAmount = Math.round(originalAmount * fetchedRate * 100) / 100;

  const effectiveRate = (() => {
    if (inputMode === 'amount') {
      const amt = parseFloat(reimbursementAmount);
      if (!isFinite(amt) || originalAmount <= 0) return 0;
      return amt / originalAmount;
    }
    const rate = parseFloat(overrideRate);
    return isFinite(rate) ? rate : 0;
  })();

  const impliedRate = effectiveRate || 0;

  const actualAmount = Math.round((originalAmount * effectiveRate) * 100) / 100;
  let deviationPercent = 0;
  let deltaAmount = 0;

  if (expectedAmount > 0 && effectiveRate > 0) {
    deltaAmount = actualAmount - expectedAmount;
    const absDelta = Math.abs(deltaAmount);
    if (absDelta < 0.005) {
      deltaAmount = 0;
      deviationPercent = 0;
    } else {
      deviationPercent = (absDelta / expectedAmount) * 100;
    }
  }

  const isValidOverride = effectiveRate > 0 && deviationPercent <= 5;

  const deviationColor = (() => {
    if (expectedAmount <= 0 || effectiveRate <= 0) return '#6b7280';
    if (deltaAmount < 0) return '#10b981';
    if (deviationPercent > 5) return '#ef4444';
    if (deltaAmount > 0) return '#f97316';
    return '#6b7280';
  })();

  const handleApply = () => {
    if (!reason.trim()) {
      setMessage('Please provide a reason for the override');
      return;
    }
    if (!isValidOverride) {
      setMessage('Override rate exceeds the ±5% limit or is invalid');
      return;
    }
    onOverrideApplied();
  };

  // Update reimbursement amount when rate changes (rate mode)
  const handleRateChange = (newRate: string) => {
    setOverrideRate(newRate);
    const rate = parseFloat(newRate);
    if (!isNaN(rate)) {
      setReimbursementAmount((originalAmount * rate).toFixed(2));
    }
  };

  // Update rate when reimbursement amount changes (amount mode)
  const handleAmountChange = (newAmount: string) => {
    setReimbursementAmount(newAmount);
    const amount = parseFloat(newAmount);
    if (!isNaN(amount) && originalAmount > 0) {
      const newRate = amount / originalAmount;
      setOverrideRate(newRate.toString());
    }
  };
 
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white', borderRadius: '12px', padding: '24px',
        maxWidth: '500px', width: '90%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>💱 FX Rate Override</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>×</button>
        </div>
        
        <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
          <div style={{ fontSize: '14px', color: '#6b7280' }}>Current Rate: {originalCurrency} → {reimbursementCurrency}</div>
          <div style={{ fontSize: '16px', fontWeight: '600' }}>{fetchedRate.toFixed(6)}</div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
            Expected: {originalAmount} {originalCurrency} = {(originalAmount * fetchedRate).toFixed(2)} {reimbursementCurrency}
          </div>
        </div>
        
        {/* Input Mode Toggle */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>Override Method</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setInputMode('amount')}
              style={{
                padding: '8px 16px',
                backgroundColor: inputMode === 'amount' ? '#3b82f6' : '#f3f4f6',
                color: inputMode === 'amount' ? 'white' : '#374151',
                border: 'none', borderRadius: '6px', fontSize: '14px', cursor: 'pointer'
              }}
            >
              💰 By Amount Received
            </button>
            <button
              onClick={() => setInputMode('rate')}
              style={{
                padding: '8px 16px',
                backgroundColor: inputMode === 'rate' ? '#3b82f6' : '#f3f4f6',
                color: inputMode === 'rate' ? 'white' : '#374151',
                border: 'none', borderRadius: '6px', fontSize: '14px', cursor: 'pointer'
              }}
            >
              📊 By Exchange Rate
            </button>
          </div>
        </div>
        
        {inputMode === 'amount' ? (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
              Amount Actually Paid ({reimbursementCurrency}) *
            </label>
            <input
              type="number"
              step="0.01"
              value={reimbursementAmount}
              onChange={(e) => handleAmountChange(e.target.value)}
              style={{
                width: '100%', padding: '10px', border: `1px solid ${isValidOverride ? '#d1d5db' : '#ef4444'}`,
                borderRadius: '6px', fontSize: '14px'
              }}
              placeholder={`Enter amount in ${reimbursementCurrency}`}
            />
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              Implied Rate: {impliedRate.toFixed(6)} {originalCurrency}/{reimbursementCurrency}
            </div>
            <div style={{ fontSize: '12px', color: deviationColor, marginTop: '2px' }}>
              Change: {deviationPercent === 0 || deltaAmount === 0
                ? '0.00%'
                : `${deltaAmount < 0 ? '-' : '+'}${deviationPercent.toFixed(2)}%`}{' '}
              {isValidOverride ? '(Valid)' : '(Exceeds 5% limit)'}
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>New Exchange Rate *</label>
            <input
              type="number"
              step="0.000001"
              value={overrideRate}
              onChange={(e) => handleRateChange(e.target.value)}
              style={{
                width: '100%', padding: '10px', border: `1px solid ${isValidOverride ? '#d1d5db' : '#ef4444'}`,
                borderRadius: '6px', fontSize: '14px'
              }}
            />
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              Reimbursement: {reimbursementAmount} {reimbursementCurrency}
            </div>
            <div style={{ fontSize: '12px', color: deviationColor, marginTop: '2px' }}>
              Change: {deviationPercent === 0 || deltaAmount === 0
                ? '0.00%'
                : `${deltaAmount < 0 ? '-' : '+'}${deviationPercent.toFixed(2)}%`}{' '}
              {isValidOverride ? '(Valid)' : '(Exceeds 5% limit)'}
            </div>
          </div>
        )}
        
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>Reason *</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain why you're overriding the exchange rate..."
            style={{
              width: '100%', padding: '10px', border: '1px solid #d1d5db',
              borderRadius: '6px', fontSize: '14px', minHeight: '60px', resize: 'vertical'
            }}
          />
        </div>
        
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 16px', backgroundColor: '#f3f4f6', color: '#374151',
              border: 'none', borderRadius: '6px', cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={!isValidOverride || !reason.trim()}
            style={{
              padding: '10px 16px',
              backgroundColor: (!isValidOverride || !reason.trim()) ? '#d1d5db' : '#3b82f6',
              color: 'white', border: 'none', borderRadius: '6px',
              cursor: (!isValidOverride || !reason.trim()) ? 'not-allowed' : 'pointer'
            }}
          >
            Apply Override
          </button>
        </div>
        
        {message && (
          <div style={{
            marginTop: '12px', padding: '10px', borderRadius: '6px',
            backgroundColor: '#fef2f2', color: '#991b1b', fontSize: '14px'
          }}>
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

const MyExpenses = () => {
  const [expenses, setExpenses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('submission' as 'submission' | 'expense');
  const [message, setMessage] = useState('');
  const [selectedExpense, setSelectedExpense] = useState(null as any);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const [showFxOverride, setShowFxOverride] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [userRole, setUserRole] = useState('');
  const [userProfile, setUserProfile] = useState(null as any);
  const [receiptLoadError, setReceiptLoadError] = useState(false);
  const navigate = useNavigate();

  const token = localStorage.getItem('token');
  const receiptUrl = selectedExpense?.receipt ? fileUrl(`/uploads/${selectedExpense.receipt}`) : '';

  const handleOpenDetails = (expense: any) => {
    setSelectedExpense(expense);
    setShowDetailModal(true);
  };

  const handleDownloadReceipt = async () => {
    if (!selectedExpense?.receipt) return;
    try {
      const response = await fetch(receiptUrl, { credentials: 'include' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = selectedExpense.receipt;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Failed to download receipt', error);
      window.open(receiptUrl, '_blank', 'noopener');
    }
  };

  // Get user role from JWT token
  useEffect(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserRole(payload.role || '');
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    }
  }, [token]);

  useEffect(() => {
    setCurrentPage(0); // Reset to first page on filter/search/sort change
    fetchUserExpenses();
    fetchUserProfile();
  }, [statusFilter, categoryFilter, searchQuery, sortBy]);

  useEffect(() => {
    setReceiptLoadError(false);
  }, [selectedExpense, showDetailModal]);

  const fetchUserProfile = async () => {
    try {
      const response = await api.get('/api/auth/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUserProfile(response.data);
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const fetchUserExpenses = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/expenses', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      let filteredExpenses = response.data;
      
      // Filter by status
      if (statusFilter) {
        filteredExpenses = filteredExpenses.filter((expense: any) => expense.status === statusFilter);
      }
      
      // Filter by category
      if (categoryFilter) {
        filteredExpenses = filteredExpenses.filter((expense: any) => expense.type === categoryFilter);
      }
      
      // Unified search across all fields
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filteredExpenses = filteredExpenses.filter((expense: any) => {
          // Search in justification and type
          const textMatch = expense.justification.toLowerCase().includes(query) ||
                           expense.type.toLowerCase().includes(query);

          // Remove all commas and spaces from search query
          const cleanQuery = query.replace(/[, ]/g, '');
          const searchAmountNum = parseFloat(cleanQuery.replace(',', '.'));

          // Normalize amount strings by removing separators
          const normalizeAmount = (val: any) => (val ?? '').toString().replace(/[, ]/g, '');

          // Search in amounts (both original and reimbursement)
          const amountMatchExact = !isNaN(searchAmountNum) && (
            expense.amount === searchAmountNum || 
            parseFloat(expense.reimbursement_amount) === searchAmountNum
          );

          const amountMatchPartial =
            normalizeAmount(expense.amount).includes(cleanQuery) ||
            normalizeAmount(expense.reimbursement_amount).includes(cleanQuery);
          
          const amountMatch = amountMatchExact || amountMatchPartial;
          
          // Search in date (formatted)
          const dateMatch = expense.date && expense.date.includes(query);
          
          // Search in expense ID
          const idMatch = expense.expense_id && expense.expense_id.toLowerCase().includes(query);
          
          return textMatch || amountMatch || dateMatch || idMatch;
        });
      }
      
      // Apply sorting: newest first
      const sorted = [...filteredExpenses].sort((a: any, b: any) => {
        const getTime = (e: any) => {
          if (sortBy === 'submission') {
            const d = e.created_at || e.date;
            return d ? new Date(d).getTime() : 0;
          }
          return e.date ? new Date(e.date).getTime() : 0;
        };
        return getTime(b) - getTime(a);
      });
      setExpenses(sorted);
    } catch (error) {
      console.error('Failed to fetch expenses:', error);
      setExpenses([]);
      setSelectedExpense(null);
      setMessage('Unable to load your expenses. Please refresh or sign in again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'EUR') => {
    if (amount === null || amount === undefined || isNaN(amount)) return `0.00 ${currency}`;
    // Format number with 2 decimal places and append 3-letter currency code
    const formattedAmount = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
    return `${formattedAmount} ${currency}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return '#9e9e9e';
      case 'submitted': return '#ff9800';
      case 'approved': return '#4caf50';
      case 'rejected': return '#f44336';
      case 'paid': return '#10b981';
      default: return '#666';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft': return '📝';
      case 'submitted': return '⏳';
      case 'approved': return '✅';
      case 'rejected': return '❌';
      case 'paid': return '💰';
      default: return '•';
    }
  };

  // Calculate expense statistics with amounts in user's preferred currency
  const calculateStatusStats = (status: string) => {
    const statusExpenses = expenses.filter((e: any) => e.status === status);
    const totalAmount = statusExpenses.reduce((sum: number, expense: any) => {
      // Use reimbursement_amount if available, otherwise calculate from amount and rate
      const reimbursementAmount = expense.reimbursement_amount || 
        (expense.amount * (expense.override_exchange_rate || expense.exchange_rate || 1));
      return sum + (parseFloat(reimbursementAmount) || 0);
    }, 0);
    return {
      count: statusExpenses.length,
      amount: totalAmount
    };
  };

  const expenseStats = {
    total: expenses.length,
    submitted: calculateStatusStats('submitted'),
    approved: calculateStatusStats('approved'),
    rejected: calculateStatusStats('rejected'),
    draft: calculateStatusStats('draft'),
  };

  const categories = [...new Set(expenses.map((e: any) => e.type))];

  // Client-side pagination for expense list (stats still use full expenses)
  const totalExpenses = expenses.length;
  const startIndex = currentPage * PAGE_SIZE;
  const endIndex = Math.min(startIndex + PAGE_SIZE, totalExpenses);
  const paginatedExpenses = expenses.slice(startIndex, endIndex);
  const hasPreviousPage = currentPage > 0;
  const hasNextPage = endIndex < totalExpenses;

  return (
    <div className="my-expenses" style={{ minHeight: '100vh', backgroundColor: '#f5f5f5', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Header */}
      <div className="my-expenses__header" style={{ padding: '16px 16px 0 16px', marginBottom: '24px' }}>
        <h1 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: '700', color: '#1f2937' }}>
          My Expenses
        </h1>
        <p style={{ margin: 0, fontSize: '16px', color: '#6b7280' }}>
          Track and manage your expense submissions
        </p>
      </div>

      <div style={{ padding: '16px' }}>
        {/* Status Cards - Reordered: submitted, approved, rejected, drafts */}
        <div className="my-expenses__status-grid" style={{ 
          display: 'grid', 
          gap: '16px', 
          marginBottom: '32px' 
      }}>
        {/* Submitted */}
        <div style={{ 
          backgroundColor: 'white', 
          padding: '20px', 
          borderRadius: '12px', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              backgroundColor: '#fef3c7', 
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px'
            }}>
              ⏳
            </div>
            <div>
              <h3 style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>
                Submitted
              </h3>
              <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#1f2937' }}>
                {expenseStats.submitted.count}
              </p>
              <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af' }}>
                {expenseStats.submitted.amount.toFixed(2)} {userProfile?.reimbursement_currency || 'EUR'}
              </p>
            </div>
          </div>
        </div>

        {/* Approved */}
        <div style={{ 
          backgroundColor: 'white', 
          padding: '20px', 
          borderRadius: '12px', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              backgroundColor: '#d1fae5', 
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px'
            }}>
              ✅
            </div>
            <div>
              <h3 style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>
                Approved
              </h3>
              <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#1f2937' }}>
                {expenseStats.approved.count}
              </p>
              <p style={{ margin: 0, fontSize: '12px', color: '#10b981' }}>
                {expenseStats.approved.amount.toFixed(2)} {userProfile?.reimbursement_currency || 'EUR'}
              </p>
            </div>
          </div>
        </div>

        {/* Rejected */}
        <div style={{ 
          backgroundColor: 'white', 
          padding: '20px', 
          borderRadius: '12px', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              backgroundColor: '#fee2e2', 
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px'
            }}>
              ❌
            </div>
            <div>
              <h3 style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>
                Rejected
              </h3>
              <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#1f2937' }}>
                {expenseStats.rejected.count}
              </p>
              <p style={{ margin: 0, fontSize: '12px', color: '#ef4444' }}>
                {expenseStats.rejected.amount.toFixed(2)} {userProfile?.reimbursement_currency || 'EUR'}
              </p>
            </div>
          </div>
        </div>

        {/* Draft */}
        <div style={{ 
          backgroundColor: 'white', 
          padding: '20px', 
          borderRadius: '12px', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              backgroundColor: '#f3f4f6', 
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px'
            }}>
              📝
            </div>
            <div>
              <h3 style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>
                Draft
              </h3>
              <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#1f2937' }}>
                {expenseStats.draft.count}
              </p>
              <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af' }}>
                {expenseStats.draft.amount.toFixed(2)} {userProfile?.reimbursement_currency || 'EUR'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="my-expenses__filters" style={{ 
        backgroundColor: 'white', 
        padding: '20px', 
        borderRadius: '12px', 
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb'
      }}>
        <div className="my-expenses__filters-row">

          <div className="my-expenses__filters-item">
            <select
              className="my-expenses__filter-control"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="paid">Paid</option>
            </select>
          </div>

          <div className="my-expenses__filters-item">
            <select
              className="my-expenses__filter-control"
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value)}
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          {/* Sort By */}
          <div className="my-expenses__filters-item">
            <select
              className="my-expenses__filter-control"
              value={sortBy}
              onChange={e => setSortBy(e.target.value as 'submission' | 'expense')}
            >
              <option value="submission">Submission date (newest)</option>
              <option value="expense">Expense date (newest)</option>
            </select>
          </div>

          {/* Search (moved to the end like Admin) */}
          <div className="my-expenses__filters-item my-expenses__filters-item--search">
            <input
              className="my-expenses__filter-control"
              type="text"
              placeholder="Search by name, amount, date, ID..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>

          <button
            className="my-expenses__filters-cta"
            onClick={() => navigate('/submit-expense')}
          >
            ➕ New Expense
          </button>
        </div>
      </div>

      {/* Expense History */}
      <div style={{ 
        backgroundColor: 'white', 
        borderRadius: '12px', 
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{ 
          padding: '20px', 
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span style={{ fontSize: '20px' }}>📋</span>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
            Expense History
          </h2>
        </div>

        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
            Loading your expenses...
          </div>
        ) : expenses.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📄</div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#1f2937' }}>No expenses found</h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#6b7280' }}>
              Get started by submitting your first expense
            </p>
            <button
              onClick={() => navigate('/submit-expense')}
              style={{
                padding: '12px 24px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Submit New Expense
            </button>
          </div>
        ) : (
          <>
          <div className="my-expenses__table-wrapper" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#f9fafb' }}>
                <tr>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', textTransform: 'uppercase' }}>Date</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', textTransform: 'uppercase' }}>Category</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', textTransform: 'uppercase' }}>Description</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', textTransform: 'uppercase' }}>Amount</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', textTransform: 'uppercase' }}>Converted Amount</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', textTransform: 'uppercase' }}>Status</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', textTransform: 'uppercase' }}>Receipt</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedExpenses.map((expense: any, index: number) => (
                    <tr
                      key={expense.id}
                      style={{
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleOpenDetails(expense)}
                    >
                      <td style={{ padding: '16px', fontSize: '14px', color: '#1f2937' }}>
                        {formatDate(expense.date)}
                      </td>
                      <td style={{ padding: '16px', fontSize: '14px', color: '#1f2937' }}>
                        {expense.type}
                      </td>
                      <td style={{ padding: '16px', fontSize: '14px', color: '#1f2937' }}>
                        {expense.justification}
                      </td>
                      <td style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                            {formatCurrency(expense.amount, expense.currency)}
                          </div>
                          {expense.vat_amount > 0 && (
                            <div style={{ fontSize: '12px', color: '#4b5563' }}>
                              <span style={{ fontWeight: '500' }}>VAT:</span> {formatCurrency(expense.vat_amount, expense.currency)}
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '16px', fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                        {formatCurrency(
                          expense.reimbursement_amount,
                          expense.reimbursement_currency || userProfile?.reimbursement_currency || 'EUR'
                        )}
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          Rate: {expense.exchange_rate?.toFixed(4) || 'N/A'}
                        </div>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            backgroundColor: getStatusColor(expense.status) + '20',
                            color: getStatusColor(expense.status)
                          }}>
                            {getStatusIcon(expense.status)}
                            {expense.status}
                          </span>
                          {/* Show rejection reason preview */}
                          {expense.status === 'rejected' && expense.admin_notes && (
                            <span style={{
                              fontSize: '11px',
                              color: '#dc2626',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              maxWidth: '200px'
                            }}>
                              <span role="img" aria-label="Rejected">😬</span>
                              {expense.admin_notes.length > 50
                                ? `${expense.admin_notes.substring(0, 50)}...`
                                : expense.admin_notes}
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '16px' }}>
                        {expense.receipt ? (
                          <a
                            href={fileUrl(`/uploads/${expense.receipt}`)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 8px',
                              backgroundColor: '#3b82f6',
                              color: 'white',
                              textDecoration: 'none',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}
                          >
                            🔎 View
                          </a>
                        ) : (
                          <span style={{ 
                            fontSize: '12px', 
                            color: '#9ca3af',
                            padding: '4px 8px',
                            backgroundColor: '#f3f4f6',
                            borderRadius: '6px'
                          }}>
                            No receipt
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {/* Edit button - only show for editable statuses */}
                          {['draft', 'submitted', 'rejected'].includes(expense.status) && (
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                navigate('/submit-expense', { state: { editingExpense: expense } });
                              }}
                              style={{
                                padding: '4px 8px',
                                backgroundColor: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '12px',
                                cursor: 'pointer'
                              }}
                            >
                              ✏️ Edit
                            </button>
                          )}
                          {/* Delete button - only show for drafts */}
                          {expense.status === 'draft' && (
                            <button
                              onClick={async (event) => {
                                event.stopPropagation();
                                if (window.confirm('Are you sure you want to delete this draft?')) {
                                  try {
                                    await api.delete(`/api/expenses/${expense.expense_id || expense.id}`, {
                                      headers: { 'Authorization': `Bearer ${token}` }
                                    });
                                    // Refresh expenses list
                                    fetchUserExpenses();
                                  } catch (error) {
                                    console.error('Failed to delete draft:', error);
                                    alert('Failed to delete draft');
                                  }
                                }
                              }}
                              style={{
                                padding: '4px 8px',
                                backgroundColor: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '12px',
                                cursor: 'pointer'
                              }}
                            >
                              🗑️ Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="my-expenses__cards">
            {paginatedExpenses.map((expense: any) => (
              <div key={expense.id} className="my-expenses__card" onClick={() => handleOpenDetails(expense)}>
                <div className="my-expenses__card-header">
                  <div className="my-expenses__card-title">
                    <span className="my-expenses__card-date">{formatDate(expense.date)}</span>
                    <span className="my-expenses__card-type">{expense.type}</span>
                  </div>
                  <span
                    className="my-expenses__status-pill"
                    style={{
                      backgroundColor: `${getStatusColor(expense.status)}20`,
                      color: getStatusColor(expense.status)
                    }}
                  >
                    {getStatusIcon(expense.status)}
                    {expense.status}
                  </span>
                </div>
                <div className="my-expenses__card-body">
                  <div className="my-expenses__card-columns">
                    <div className="my-expenses__card-column">
                      <div className="my-expenses__card-row">
                        <span className="my-expenses__card-label">Amount</span>
                        <span className="my-expenses__card-value">{formatCurrency(expense.amount, expense.currency)}</span>
                      </div>
                      {expense.vat_amount > 0 && (
                        <div className="my-expenses__card-row">
                          <span className="my-expenses__card-label">VAT</span>
                          <span className="my-expenses__card-value">{formatCurrency(expense.vat_amount, expense.currency)}</span>
                        </div>
                      )}
                      <div className="my-expenses__card-row">
                        <span className="my-expenses__card-label">Converted</span>
                        <span className="my-expenses__card-value">{formatCurrency(
                          expense.reimbursement_amount,
                          expense.reimbursement_currency || userProfile?.reimbursement_currency || 'EUR'
                        )}</span>
                      </div>
                      <div className="my-expenses__card-meta">Rate: {expense.exchange_rate?.toFixed(4) || 'N/A'}</div>
                    </div>
                    <div className="my-expenses__card-column my-expenses__card-column--justification">
                      <span className="my-expenses__card-label">Justification</span>
                      <p className="my-expenses__card-justification">
                        {expense.justification || 'No justification provided'}
                      </p>
                    </div>
                  </div>
                  {expense.status === 'rejected' && expense.admin_notes && (
                    <div className="my-expenses__card-reason">
                      <span role="img" aria-label="Rejected">😬</span>
                      {expense.admin_notes.length > 80
                        ? `${expense.admin_notes.substring(0, 80)}...`
                        : expense.admin_notes}
                    </div>
                  )}
                </div>
                <div
                  className="my-expenses__card-actions my-expenses__card-actions--compact"
                  onClick={(event) => event.stopPropagation()}
                >
                  {expense.receipt ? (
                    <a
                      href={fileUrl(`/uploads/${expense.receipt}`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="my-expenses__card-button my-expenses__card-button--secondary"
                    >
                      🔎 View Receipt
                    </a>
                  ) : (
                    <span className="my-expenses__card-no-receipt">No receipt</span>
                  )}
                  {['draft', 'submitted', 'rejected'].includes(expense.status) && (
                    <button
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate('/submit-expense', { state: { editingExpense: expense } });
                      }}
                      className="my-expenses__card-button my-expenses__card-button--success"
                    >
                      ✏️ Edit
                    </button>
                  )}
                  {expense.status === 'draft' && (
                    <button
                      onClick={async (event) => {
                        event.stopPropagation();
                        if (window.confirm('Are you sure you want to delete this draft?')) {
                          try {
                            await api.delete(`/api/expenses/${expense.expense_id || expense.id}`, {
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            fetchUserExpenses();
                          } catch (error) {
                            console.error('Failed to delete draft:', error);
                            alert('Failed to delete draft');
                          }
                        }
                      }}
                      className="my-expenses__card-button"
                      style={{ backgroundColor: '#ef4444' }}
                    >
                      🗑️ Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {totalExpenses > PAGE_SIZE && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 16px',
                borderTop: '1px solid #e5e7eb'
              }}
            >
              <button
                onClick={() => hasPreviousPage && setCurrentPage(prev => Math.max(prev - 1, 0))}
                disabled={!hasPreviousPage}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  backgroundColor: hasPreviousPage ? '#ffffff' : '#f3f4f6',
                  color: hasPreviousPage ? '#374151' : '#9ca3af',
                  fontSize: '13px',
                  cursor: hasPreviousPage ? 'pointer' : 'not-allowed'
                }}
              >
                ← Previous
              </button>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                {totalExpenses === 0
                  ? 'Showing 0 expenses'
                  : `Showing ${startIndex + 1}-${endIndex} of ${totalExpenses} expenses`}
              </div>
              <button
                onClick={() => hasNextPage && setCurrentPage(prev => prev + 1)}
                disabled={!hasNextPage}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  backgroundColor: hasNextPage ? '#ffffff' : '#f3f4f6',
                  color: hasNextPage ? '#374151' : '#9ca3af',
                  fontSize: '13px',
                  cursor: hasNextPage ? 'pointer' : 'not-allowed'
                }}
              >
                Next →
              </button>
            </div>
          )}
          </>
        )}
      </div>

      {/* Message Display */}
      {message && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '12px 20px',
          backgroundColor: message.includes('success') ? '#10b981' : '#ef4444',
          color: 'white',
          borderRadius: '8px',
          zIndex: 1001
        }}>
          {message}
          <button
            onClick={() => setMessage('')}
            style={{ marginLeft: '12px', background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}
          >
            ×
          </button>
        </div>
      )}
      
      {/* Expense Detail Modal */}
      {showDetailModal && selectedExpense && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#1f2937' }}>
                📊 Expense Details
              </h2>
              <button
                onClick={() => setShowDetailModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '4px'
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ display: 'grid', gap: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Expense ID</label>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>{selectedExpense.expense_id || 'N/A'}</p>
                </div>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Date</label>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#1f2937' }}>{new Date(selectedExpense.date).toLocaleDateString()}</p>
                </div>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Category</label>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#1f2937' }}>{selectedExpense.type}</p>
                </div>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Status</label>
                  <div style={{ margin: '4px 0 0 0' }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: getStatusColor(selectedExpense.status) + '20',
                      color: getStatusColor(selectedExpense.status)
                    }}>
                      {getStatusIcon(selectedExpense.status)}
                      {selectedExpense.status}
                    </span>
                  </div>
                </div>
              </div>
              
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Description</label>
                <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#1f2937', lineHeight: '1.5' }}>{selectedExpense.justification}</p>
              </div>
              
              {/* Show rejection reason if expense is rejected and admin notes exist */}
              {selectedExpense.status === 'rejected' && selectedExpense.admin_notes && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#fef2f2',
                  borderRadius: '8px',
                  border: '1px solid #fecaca'
                }}>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#dc2626', textTransform: 'uppercase' }}>Rejection Reason</label>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#dc2626', lineHeight: '1.5' }}>
                    {selectedExpense.admin_notes}
                  </p>
                </div>
              )}
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Original Amount</label>
                  <p style={{ margin: '4px 0 0 0', fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                    {formatCurrency(selectedExpense.amount, selectedExpense.currency)}
                  </p>
                </div>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Reimbursement Amount</label>
                  <p style={{ margin: '4px 0 0 0', fontSize: '16px', fontWeight: '600', color: '#10b981' }}>
                    {formatCurrency(selectedExpense.reimbursement_amount, selectedExpense.reimbursement_currency)}
                  </p>
                  {(selectedExpense.exchange_rate || selectedExpense.override_exchange_rate) && (
                    <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
                      Exchange Rate: {(selectedExpense.override_exchange_rate ?? selectedExpense.exchange_rate).toFixed(4)}
                    </p>
                  )}
                  {selectedExpense.override_exchange_rate && selectedExpense.fetched_exchange_rate && (
                    <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
                      Original fetched rate: {selectedExpense.fetched_exchange_rate.toFixed(4)} {selectedExpense.currency}/{selectedExpense.reimbursement_currency}
                      {(() => {
                        const rawVariation = ((selectedExpense.override_exchange_rate - selectedExpense.fetched_exchange_rate) / selectedExpense.fetched_exchange_rate) * 100;
                        let variation = rawVariation;
                        const absVariation = Math.abs(variation);

                        if (absVariation < 0.005) {
                          variation = 0;
                        }

                        let bg = '#e5e7eb';
                        let fg = '#374151';
                        let sign = '';

                        if (variation < 0) {
                          bg = '#dcfce7';
                          fg = '#166534';
                          sign = '-';
                        } else if (variation > 0) {
                          if (absVariation > 5) {
                            bg = '#fee2e2';
                            fg = '#991b1b';
                            sign = '+';
                          } else {
                            bg = '#ffedd5';
                            fg = '#c2410c';
                            sign = '+';
                          }
                        }

                        const displayValue = Math.abs(variation).toFixed(2);

                        return (
                          <span
                            style={{
                              marginLeft: '8px',
                              padding: '2px 6px',
                              backgroundColor: bg,
                              color: fg,
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: 600
                            }}
                          >
                            {variation === 0 ? '0.00%' : `${sign}${displayValue}%`}
                          </span>
                        );
                      })()}
                    </p>
                  )}
                </div>
                {selectedExpense.approved_at && (
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Approved</label>
                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
                      {new Date(selectedExpense.approved_at).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Receipt</label>
                {selectedExpense.receipt ? (
                  <div className="my-expenses__modal-receipt-wrapper">
                    <div className="my-expenses__modal-receipt">
                      {!receiptLoadError ? (
                        selectedExpense.receipt.toLowerCase().endsWith('.pdf') ? (
                          <iframe
                            src={receiptUrl}
                            title="Receipt preview"
                            className="my-expenses__modal-receipt-image"
                            style={{ width: '100%', height: '500px', border: 'none' }}
                          />
                        ) : (
                          <img
                            src={receiptUrl}
                            alt="Receipt preview"
                            className="my-expenses__modal-receipt-image"
                            onError={() => setReceiptLoadError(true)}
                          />
                        )
                      ) : (
                        <div className="my-expenses__modal-receipt-fallback">
                          Unable to load receipt preview.
                        </div>
                      )}
                    </div>
                    <div className="my-expenses__modal-receipt-actions">
                      <a
                        href={receiptUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="my-expenses__modal-receipt-link"
                      >
                        Open in new tab
                      </a>
                      <button
                        type="button"
                        onClick={handleDownloadReceipt}
                        className="my-expenses__modal-receipt-link my-expenses__modal-receipt-link--primary"
                        style={{ border: 'none' }}
                      >
                        Download
                      </button>
                    </div>
                  </div>
                ) : (
                  <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#6b7280' }}>No receipt uploaded.</p>
                )}
              </div>
            </div>
            
            <div style={{ height: '8px' }} />
          </div>
        </div>
      )}

      {/* FX Override Modal - Using placeholder temporarily */}
      {showFxOverride && selectedExpense && (
        <FxOverrideModal
          fetchedRate={selectedExpense.fetched_exchange_rate || selectedExpense.exchange_rate}
          currentRate={selectedExpense.override_exchange_rate || selectedExpense.exchange_rate}
          originalAmount={selectedExpense.amount}
          originalCurrency={selectedExpense.currency}
          reimbursementCurrency={selectedExpense.reimbursement_currency}
          onOverrideApplied={() => {
            setShowFxOverride(false);
            fetchUserExpenses(); // Refresh expenses to show updated rates
          }}
          onClose={() => setShowFxOverride(false)}
        />
      )}

      {/* Edit functionality now uses navigation to Submit Expense page */}
      </div>
    </div>
  );
};

// EditExpenseForm component removed - editing now uses navigation to Submit Expense page

export default MyExpenses;






